/**
 * Owns the life cycle of "this attempt might end up on the board".
 *
 * ==========================================================================
 *  THE ORDER MATTERS
 * ==========================================================================
 *   READY   -> a ticket is fetched in the background. It costs one round trip
 *              and it happens while the player is still reading the title, so
 *              the finish line never waits for the network.
 *   RUNNING -> the recorder captures every step.
 *   FINISH  -> the replay is re-simulated *locally* first. If the local
 *              re-simulation disagrees with what was just played, the bug is
 *              ours, and it is much better to find that here than to have the
 *              server reject an honest run and blame the player.
 *           -> then it goes up, and the server decides.
 *
 *  Nothing here reports a time to anyone. The submission carries inputs; the
 *  server produces the number.
 * ==========================================================================
 */

import { verifyReplay } from '../replay/verifyReplay';
import type { Replay } from '../replay/ReplayFormat';
import { encodeReplay } from '../replay/ReplayFormat';
import type { RunResult } from './ScoreRepository';
import { sanitisePlayerName } from './playerName';
import type { LeaderboardClient, LeaderboardEntry, RunTicket } from './Leaderboard';

export type SubmissionState =
  /** No backend at all - offline LAN, dev server, file://. */
  | { status: 'offline' }
  /** Playable and recorded, but this attempt can never be ranked. Says why. */
  | { status: 'unranked'; reason: string }
  | { status: 'idle' }
  | { status: 'sending' }
  | { status: 'accepted'; replayId?: string; rank: number; entry: LeaderboardEntry; flagged: boolean }
  | { status: 'rejected'; reason: string }
  | { status: 'error'; reason: string };

export type SubmissionListener = (state: SubmissionState) => void;

export class RunSubmitter {
  private ticket: RunTicket | null = null;
  private pendingTicket: Promise<RunTicket | null> | null = null;
  private currentState: SubmissionState;
  private readonly listeners = new Set<SubmissionListener>();

  constructor(
    private readonly client: LeaderboardClient,
    private readonly now: () => number = () => Date.now(),
  ) {
    this.currentState = client.isAvailable ? { status: 'idle' } : { status: 'offline' };
  }

  get state(): SubmissionState {
    return this.currentState;
  }

  /** The run id to stamp into the replay header. Empty when playing offline. */
  get runId(): string {
    return this.ticket?.runId ?? '';
  }

  subscribe(listener: SubmissionListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Called on entering READY and whenever the dose changes. Cheap to call
   * repeatedly: an unexpired ticket for the same category is reused.
   */
  async prepare(category: string): Promise<void> {
    if (!this.client.isAvailable) {
      this.set({ status: 'offline' });
      return;
    }
    this.set({ status: 'idle' });

    if (this.ticket && this.ticket.category === category && this.ticket.expiresAt > this.now()) {
      return;
    }
    this.ticket = null;

    // Collapse concurrent calls - the dose sliders fire a lot of changes.
    const request = this.client.openTicket(category);
    this.pendingTicket = request;
    const ticket = await request;
    if (this.pendingTicket !== request) return;
    this.pendingTicket = null;
    this.ticket = ticket;

    if (!ticket) {
      this.set({ status: 'unranked', reason: 'Palvelin ei vastannut - tulos jää paikalliseksi.' });
    }
  }

  /**
   * Sends a finished run. `replay` must be the recording of exactly the attempt
   * `result` describes.
   */
  async submit(result: RunResult, replay: Replay | null, name: string): Promise<SubmissionState> {
    if (!this.client.isAvailable) return this.set({ status: 'offline' });

    if (!replay) {
      return this.set({ status: 'unranked', reason: 'Yrityksestä ei tallentunut replayta.' });
    }
    const ticket = this.ticket;
    if (!ticket || ticket.runId !== replay.header.runId) {
      return this.set({ status: 'unranked', reason: 'Yritys ajettiin ilman palvelimen run-tunnusta.' });
    }
    if (ticket.expiresAt <= this.now()) {
      return this.set({ status: 'unranked', reason: 'Run-tunnus ehti vanhentua.' });
    }

    // Re-simulate our own recording before bothering the server with it.
    const verdict = verifyReplay(replay);
    if (!verdict.ok) {
      return this.set({ status: 'unranked', reason: `Replay ei validoidu paikallisesti: ${verdict.message}` });
    }
    if (verdict.timeMs !== result.timeMs) {
      // Never expected. If it happens, the recorder and the simulation have
      // drifted apart and submitting would just get the player rejected.
      return this.set({
        status: 'unranked',
        reason: 'Replay ja ajanotto eivät täsmää - tulosta ei lähetetty.',
      });
    }

    this.set({ status: 'sending' });

    let bytes: Uint8Array;
    try {
      bytes = encodeReplay(replay);
    } catch (error) {
      return this.set({
        status: 'unranked',
        reason: error instanceof Error ? error.message : 'Replayn pakkaus epäonnistui.',
      });
    }

    const outcome = await this.client.submit({
      token: ticket.token,
      name: sanitisePlayerName(name),
      replay: bytes,
    });

    // The ticket is single-use on the server, so it is spent either way.
    this.ticket = null;

    if (outcome.status === 'accepted') {
      return this.set({
        status: 'accepted',
        replayId: outcome.replayId,
        rank: outcome.rank,
        entry: outcome.entry,
        flagged: outcome.flagged,
      });
    }
    return this.set(outcome);
  }

  private set(state: SubmissionState): SubmissionState {
    this.currentState = state;
    for (const listener of this.listeners) listener(state);
    return state;
  }
}
